import { useState, useEffect } from "react";
import { Card } from "./components/ui/Card";
import { CardContent } from "./components/ui/CardContent";
import { Button } from "./components/ui/Button";
import { Input } from "./components/ui/Input";
import image from "./assets/New Company Logo.jpeg";
import "./styles.css";

const API_URL = "https://chatbot-python-7s06.onrender.com/query";  // Using localhost API URL

function ChatPrompt({ hasMessages }) {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (hasMessages) setVisible(false);
  }, [hasMessages]);

  if (!visible) return null;

  return (
    <div className="chat-prompt">
      <img src={image} alt="Company Logo" style={{ width: 500 }} />
      Hey, I'm EmbarkGPT. Ask me anything!
    </div>
  );
}

export default function ChatInterface() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [imageBase64, setImageBase64] = useState(null);

  const escapeHTML = (str) => {
    return str
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  };

  // Function to format text with basic HTML formatting
  const formatText = (text) => {
    // Handle code blocks first
    const hasCodeBlocks = text.includes("```");
    if (hasCodeBlocks) {
      return text.replace(
        /```(\w+)?\n([\s\S]*?)```/g,
        (match, lang, code) => {
          return `<pre><code class="language-${lang || "plaintext"}">${escapeHTML(code)}</code></pre>`;
        }
      );
    }

    // Format text with basic HTML
    let formattedText = text;
    
    // Convert headings (like "**Title:**")
    formattedText = formattedText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Convert bullet points (like "* Item")
    formattedText = formattedText.replace(/\* (.*?)(?=\n|$)/g, '<li>$1</li>');
    
    // Wrap bullet points in ul tags
    if (formattedText.includes('<li>')) {
      formattedText = formattedText.replace(/(<li>.*?<\/li>)(?:\s*<li>.*?<\/li>)*/g, '<ul>$&</ul>');
    }
    
    // Convert paragraphs (split by double newlines)
    formattedText = formattedText.split(/\n\n+/).map(para => {
      // If the paragraph doesn't start with a special tag, wrap it in <p>
      if (!para.trim().startsWith('<')) {
        return `<p>${para}</p>`;
      }
      return para;
    }).join('');
    
    // Convert single newlines to <br> tags
    formattedText = formattedText.replace(/\n/g, '<br>');
    
    return formattedText;
  };

  const sendMessage = async (customInput) => {
    const messageToSend = customInput || input;
    
    // Allow empty messages if we have an image
    if (!messageToSend.trim() && !imageBase64) return;

    let userMessageText = messageToSend || ""; // Use empty string if no text input
    
    // Create user message object
    const userMessage = { 
      sender: "user", 
      text: userMessageText
    };
    
    // If there's an image, add it to the user message
    if (imageBase64) {
      userMessage.imageBase64 = imageBase64;
    }

    // Add message to chat
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      // Check if the user wants to generate an image
      if (messageToSend.toLowerCase().startsWith("/create")) {
        const prompt = messageToSend.slice(7).trim(); // Extract the prompt from the message after "/create"
        const response = await fetch("https://chatbot-python-7s06.onrender.com/generate-image", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ prompt }),
        });

        const data = await response.json();

        if (data.image_base64) {
          const imageData = data.image_base64;
          setMessages((prev) => [
            ...prev,
            { sender: "bot", text: "Here is your generated image:", imageBase64: imageData },
          ]);
        } else {
          setMessages((prev) => [...prev, { sender: "bot", text: "Sorry, there was an error generating the image." }]);
        }
      } else {
        // Normal message handling for chat
        const formattedMessages = messages.map((msg) => ({
          role: msg.sender === "user" ? "user" : "assistant",
          content: msg.text,
        }));

        formattedMessages.push({ role: "user", content: userMessageText });

        // Send request to backend with image and text
        const response = await fetch(API_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            messages: formattedMessages,
            image_base64: imageBase64,
          }),
        });
        
        const data = await response.json();

        let { response: apiResponse, error, usage } = data;

        if (apiResponse) {
          apiResponse = apiResponse.replace(/<think>.*?<\/think>/gs, "").trim();
          
          // Process the text with your formatting function
          const formattedResponse = formatText(apiResponse);
          
          const botMessage = { 
            sender: "bot", 
            text: formattedResponse, 
            isFormatted: true 
          };
          
          const messagesToAdd = [botMessage];

          if (usage) {
            messagesToAdd.push({
              sender: "bot",
              text: `Usage details: Tokens used: ${usage.total_tokens}, Queue time: ${usage.queue_time}s`,
              isFormatted: false
            });
          }

          setMessages((prev) => [...prev, ...messagesToAdd]);
        } else if (error) {
          setMessages((prev) => [...prev, { sender: "bot", text: error, isFormatted: false }]);
        } else {
          setMessages((prev) => [...prev, { sender: "bot", text: "Unexpected error", isFormatted: false }]);
        }
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages((prev) => [...prev, { sender: "bot", text: "Error fetching response. Try again!", isFormatted: false }]);
    }
    
    setLoading(false);
    // Clear the image after sending
    setImageBase64(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handlePaste = async (e) => {
    const items = e.clipboardData.items;
    for (const item of items) {
      if (item.type.startsWith("image/")) {
        const file = item.getAsFile();
        if (file) {
          // Just process the image, don't send a message
          processImageFile(file);
          break; // Only process the first image
        }
      }
    }
  };

  const processImageFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      // Extract only the base64 data part
      const base64Data = e.target.result.split(',')[1];
      setImageBase64(base64Data);
    };
    reader.readAsDataURL(file);
  };

  const clearImage = () => {
    setImageBase64(null);
  };

  // Function to render message content with proper formatting
  const renderMessageContent = (message) => {
    // For messages with images
    if (message.imageBase64) {
      return (
        <>
          {message.text && <div>{
            message.sender === "user" || !message.isFormatted 
              ? message.text 
              : <div className="formatted-content" dangerouslySetInnerHTML={{ __html: message.text }} />
          }</div>}
          <img 
            src={message.imageBase64.includes('data:') ? message.imageBase64 : `data:image/jpeg;base64,${message.imageBase64}`} 
            alt="Image" 
            style={{ maxWidth: "100%", marginTop: "10px" }} 
          />
        </>
      );
    }
    
    // For text-only messages
    if (message.sender === "user" || !message.isFormatted) {
      return message.text;
    }
    
    // For formatted bot messages, use dangerouslySetInnerHTML to render the HTML
    return <div className="formatted-content" dangerouslySetInnerHTML={{ __html: message.text }} />;
  };

  return (
    <div className="chat-container" onPaste={handlePaste}>
      <header className="header">
        <img src={image} alt="Company Logo" className="logo" />
        <a href="https://embark-growth.com" target="_blank" rel="noopener noreferrer">
          <button className="visit-btn">Visit Website</button>
        </a>
      </header>

      <Card className="chat-card">
        <CardContent>
          <ChatPrompt hasMessages={messages.length > 0} />

          <div className="chat-box">
            {messages.map((msg, idx) => (
              <div key={idx} className={`chat-message ${msg.sender === "user" ? "user-message" : "bot-message"}`}>
                {renderMessageContent(msg)}
              </div>
            ))}
            {loading && <div className="bot-message">Typing...</div>}
          </div>

          <div className="input-container">
            <Input
              className="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={imageBase64 ? "Ask a question about the pasted image..." : "Type a message or paste an image..."}
              disabled={loading}
              rows={4}
              style={{ resize: "none" }}
            />
            
            {imageBase64 && (
              <div className="image-preview">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "5px" }}>
                  <span>Image attached</span>
                  <Button 
                    onClick={clearImage} 
                    className="clear-image-btn"
                    style={{ padding: "2px 8px", fontSize: "12px" }}
                  >
                    Remove
                  </Button>
                </div>
                <img 
                  src={`data:image/jpeg;base64,${imageBase64}`} 
                  alt="Pasted" 
                  style={{ maxWidth: "200px", maxHeight: "100px" }} 
                />
              </div>
            )}
            
            <div className="button-group">
              <Button 
                className="chat-button" 
                onClick={() => sendMessage()} 
                disabled={loading}
              >
                {loading ? "Processing..." : "Send"}
              </Button>
              <Button
                className="chat-button"
                onClick={() => {
                  if (!input.trim()) return;
                  sendMessage(`/create ${input}`);
                }}
                disabled={loading}
              >
                {loading ? "Generating..." : "Generate Image"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
